import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import "@/index.css";

import Landing from "@/pages/Landing";
import ThalesLandingB2B from "@/pages/ThalesLandingB2B";
import IntakeForm from "@/pages/IntakeForm";
import DoctorIntakeForm from "@/pages/DoctorIntakeForm";
import PaymentPage from "@/pages/PaymentPage";
import RecetaConfirmada from "@/pages/RecetaConfirmada";
import AuthPage from "@/pages/AuthPage";
import DoctorDashboard from "@/pages/DoctorDashboard";
import ConsultDetail from "@/pages/ConsultDetail";
import AdminPanel from "@/pages/AdminPanel";
import UrgenciaPage from "@/pages/UrgenciaPage";
import ConsultStatusPage from "@/pages/ConsultStatusPage";
import DoctorRegister from "@/pages/DoctorRegister";
import DoctorVerify from "@/pages/DoctorVerify";
import AdminDoctors from "@/pages/AdminDoctors";
import AdminInstitutionReviews from "@/pages/AdminInstitutionReviews";
import TermsPage from "@/pages/TermsPage";
import SelectDoctor from "@/pages/SelectDoctor";
import PrimeraVez from "@/pages/PrimeraVez";
import SessionTimeoutManager from "@/components/SessionTimeoutManager";
import { useTenant } from "@/hooks/useTenant";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { tenant, loading: tenantLoading } = useTenant();

  useEffect(() => {
    // 1) Doctor quick-access flow: ?access=... on /consult/:id
    //    Exchange the one-click token for a JWT before any route renders.
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access');
    const onConsultRoute = window.location.pathname.startsWith('/consult/');

    if (accessToken && onConsultRoute) {
      exchangeConsultAccess(accessToken);
      return;
    }

    // 2) Normal session bootstrap
    const token = localStorage.getItem('auth_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const exchangeConsultAccess = async (accessToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/consult-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: accessToken }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        // Replace any existing session (recommendation a)
        localStorage.setItem('auth_token', data.token);
        // Paint immediately with the minimal user from the exchange…
        setUser(data.user);
        // …then hydrate the FULL user from /auth/me so this session is identical
        // to a normal login (institution_ids, doctor_status, etc.). Without this,
        // the quick-access session carried a partial user and was never validated,
        // which could leave the app showing a "logged-in but empty" shell if the
        // token was later rejected. Best-effort: never log the doctor out here.
        try {
          const meResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${data.token}` },
          });
          if (meResp.ok) {
            setUser(await meResp.json());
          }
        } catch (_) { /* keep minimal user; token stays */ }
        // Clean the URL so the token doesn't linger / can't be re-shared from history
        const cleanPath = `/consult/${data.consult_id}`;
        window.history.replaceState({}, '', cleanPath);
      } else {
        // Token invalid/used/expired/already-answered. Flag the reason so the
        // login screen can show a friendly "direct access expired" message, and
        // remember the consult so we land the doctor back on it after login.
        localStorage.removeItem('auth_token');
        const cleanPath = window.location.pathname; // /consult/:id
        try {
          localStorage.setItem('auth_redirect', cleanPath);
          localStorage.setItem('consult_access_expired', '1');
        } catch {
          // ignore
        }
        window.history.replaceState({}, '', cleanPath);
        const existingToken = localStorage.getItem('auth_token');
        if (existingToken) {
          await verifyToken(existingToken);
          return;
        }
      }
    } catch (err) {
      console.error('Quick access exchange failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  };

  const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando...</p>
          </div>
        </div>
      );
    }

    if (!user) {
      // Save the intended destination so we can return there after login.
      // Excludes /auth itself to avoid loops, and only persists non-trivial routes.
      try {
        const path = window.location.pathname + window.location.search;
        if (path && !path.startsWith('/auth') && path !== '/' && path !== '/landing') {
          localStorage.setItem('auth_redirect', path);
        }
      } catch {
        // ignore
      }
      return <Navigate to="/auth" />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return <Navigate to="/dashboard" />;
    }

    return children;
  };

  if (loading || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  // Root routing rule (multi-tenant architecture):
  //   - When a tenant is resolved (subdomain like `fleni.thaleshealth.com` or
  //     `?tenant=fleni` in dev/preview) -> render the patient-facing Landing
  //     with tenant branding.
  //   - Otherwise (bare `thaleshealth.com` / preview root) -> render the
  //     Thales Health B2B corporate landing. Patients should never end up
  //     here in normal flows; they enter through smart links or subdomains.
  const rootElement = tenant ? <Landing tenant={tenant} /> : <ThalesLandingB2B />;

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <SessionTimeoutManager enabled={!!user} onLogout={() => setUser(null)} />
      <Routes>
        <Route path="/" element={rootElement} />
        <Route path="/intake" element={<IntakeForm />} />
        <Route path="/urgencia" element={<UrgenciaPage />} />
        <Route path="/c/:consultId" element={<ConsultStatusPage />} />
        <Route path="/payment/:consultId" element={<PaymentPage />} />
        <Route path="/receta-confirmada/:consultId" element={<RecetaConfirmada />} />
        <Route path="/auth" element={<AuthPage onLogin={(userData) => setUser(userData)} />} />
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['MEDICO', 'ADMIN', 'STAFF']}>
            {user?.role === 'ADMIN' ? <Navigate to="/admin" replace /> : <DoctorDashboard user={user} />}
          </ProtectedRoute>
        } />
        <Route path="/consult/:consultId" element={
          <ProtectedRoute allowedRoles={['MEDICO', 'ADMIN', 'STAFF']}>
            <ConsultDetail user={user} />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminPanel user={user} />
          </ProtectedRoute>
        } />
        <Route path="/admin/doctors" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminDoctors user={user} />
          </ProtectedRoute>
        } />
        <Route path="/admin/institution-reviews" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminInstitutionReviews user={user} />
          </ProtectedRoute>
        } />
        {/* Doctor Registration Routes */}
        <Route path="/doctor/register" element={<DoctorRegister />} />
        <Route path="/doctor/verify" element={<DoctorVerify />} />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/mis-medicos" element={<SelectDoctor />} />
        <Route path="/primera-vez" element={<PrimeraVez />} />
        {/* Smart Links - Doctor personalized URLs */}
        <Route path="/:institutionSlug/:doctorSlug" element={<DoctorIntakeForm />} />
        {/* Legacy route - redirect to intake */}
        <Route path="/simulate" element={<IntakeForm />} />
        {/* Redirect /consulta to intake */}
        <Route path="/consulta" element={<IntakeForm />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;